import {
  BadgeCheck,
  Bookmark,
  Check,
  Compass,
  Ellipsis,
  Heart,
  MapPin,
  MessageCircle,
  Share,
} from 'lucide-react';
import { memo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { POST_DEFAULTS, type PostData, type PublicAuthor } from '@dove/shared';
import { deletePost, editPost, setLike } from '@/lib/api';
import { keys, queryClient } from '@/lib/query-client';
import {
  cancelPostQueries,
  removePostCaches,
  updatePostCaches,
} from '@/lib/post-cache';
import { useSession } from '@/lib/session';
import { useThreadSheet } from '@/lib/thread-sheet';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  COPIED_MS,
  canCopyLink,
  canShareLink,
  letterUrl,
  shareLink,
} from '@/lib/share';
import { spaceIcon } from '@/lib/space';
import { cn } from '@/lib/utils';
import { ReviseField, ReviseMenu } from './Revise';
import { LETTER_OPEN } from './letter/LetterSheet';
import { LetterStamp } from './letter/LetterStamp';
import { BirdPortrait } from './BirdPortrait';
import { ToProfile } from './ToProfile';
import { Written } from './Written';
import { PostMediaGrid } from './PostMedia';
import { Poll } from './Poll';

/**
 * An open letter, drawn as a MINI LETTER: a small card lying on the app's ground,
 * on the same surface and at the same radius the Loft draws the real thing (see
 * `LetterSheet`, and `letter-sheet` / `letter-note` in index.css).
 *
 * THE OBJECT IS CALLED AN **OPEN LETTER** ON SCREEN AND `post` IN THE CODE
 * (docs/5 § D260803-1952): the interface uses the phrase a reader understands, the schema and
 * the code keep the product's own noun. Nothing a reader of this card can see says
 * *post*: the answer action is *Comment*, on every surface (`CommentAction`), the
 * menu reports a letter, and the byline of a folded pair says
 * what it is commenting on. `PostData`, `PostMediaGrid`, `replyToId` and
 * POST_MAX_CHARS keep the product's own noun.
 *
 * THIS FILE HAD ALREADY ARGUED THE RENAME BEFORE THERE WAS ONE, which is why it is
 * a rename rather than a redesign: the paragraph below concluded a post IS a letter
 * at a quarter of the length, and drew it that way. The word caught up.
 *
 * WHY AN OPEN LETTER IS THE SAME OBJECT AS A LETTER. The product has exactly one
 * written object at two lengths — a remark and an essay, which is why POST_MAX_CHARS
 * is a sixteenth of LETTER_MAX_CHARS (docs/1). It was being drawn two ways:
 * letters as sheets with serif bodies, posts as sans rows in an
 * undifferentiated scrolling column, one tab apart. That is the same bug
 * `LetterSheet` was written to settle when the Loft and a person's card
 * disagreed about what a letter looks like, one level up — and it resolves the
 * same way. Same object, same surface; the two are told apart by SIZE, which
 * is the distinction the product already draws.
 *
 * THE MATERIAL USED TO BE PAPER AND IS NOW THE APP'S CARD (docs/5 § D260803-0020).
 * `LetterSheet` carries the argument in full, since it is one change across both
 * sizes of the one object. What it means here: `--card` rather than a warm cream
 * stock, the card radius rather than square corners, and no hairline — a shadow
 * holds the edge, as it does on `ui/card`.
 *
 * WHAT THAT COSTS ON A FEED, STATED PLAINLY, because the divider this component
 * replaced had a standing argument attached to it: a card per post "makes every
 * ordinary sentence look like an announcement". The old answer was that paper is
 * what an ordinary sentence is written on, and a rounded elevated field is what
 * this app uses for chrome — which was a genuine defence and is no longer
 * available, because the sheet IS that field now. Two things keep the objection
 * from landing anyway, and they are what to protect if it ever does:
 *
 *   - **The shadow is `shadow-sm` and there is no border at all.** A post is one
 *     step off the ground, not a raised panel. Anything heavier and the objection
 *     is correct.
 *   - **The chrome around the feed is not drawn as a card.** A sheet in this
 *     column means "somebody wrote this" only for as long as the things that are
 *     NOT writing stay flat on the ground.
 *
 * INHERITED FROM THE LOFT, DELIBERATELY AND NOT BY COINCIDENCE:
 *
 *   - **The surface, the radius and the shadow**, which is one decision made in
 *     `LetterSheet` and followed here. When those three change, they change
 *     together in both files or the two sizes of one object stop being one
 *     object — which is the failure this component exists to have fixed.
 *   - **Inset, not full-bleed.** Held off the screen edge by the app's own
 *     gutter, so there is a background behind it and it is a thing lying on
 *     the page rather than a band of the page.
 *   - **A serif body, a sans letterhead.** The prose opts into `font-letter`;
 *     metadata is chrome and stays in the UI face. With the material gone this is
 *     load-bearing rather than decorative: the typeface and the measure are what
 *     is left saying a post is writing rather than a row.
 *
 * WHERE IT DIVERGES, AND WHY — WHICH IS NOW ALMOST NOWHERE. A letter's letterhead
 * is bird-and-name left, stamp right; so is this one. The divergence used to be
 * real: a post drew a bare round face where a letter draws postage. What is left
 * is a difference of SIZE and of one flag, which is the right amount of difference
 * for the same object at a quarter of the length.
 *
 *   - **No avatar column.** The 52px indent it cost was charged to every line
 *     of every post, on the narrowest screen in the product, to repeat a face
 *     that is an inch from itself down the feed. Prose now starts at the
 *     sheet's own edge, which is also the only way a 15px serif gets a
 *     tolerable measure on a phone.
 *   - **The bird leads the name, inline.** docs/1's rule is that a person's
 *     pigeon appears beside their name, and `LetterStamp` explains what it is
 *     doing on a letter: it is postage, evidence the thing travelled — so the
 *     bird appears exactly ONCE per sheet. That constraint is kept here; only
 *     the form changes. The bird stands inline where the docs put it in the
 *     first place, which since docs/5 § D260805-1523 is the only place a bird ever
 *     stands: there is no round avatar left for one to perch on.
 *   - **The photograph is POSTAGE, and that is a reversal.** This file used to
 *     argue that "a perforated 34px stamp is an ornament a post has no room
 *     for", and drew a bare 26px round face in the top corner instead (a `Face`
 *     component, deleted in docs/5 § D260805-1523). The premise
 *     was right and the conclusion did not follow. A post is a mini letter on
 *     the Loft's own surface — that is the entire idea this component is built on
 *     — and the one mark that makes paper read as post is a stamp in the
 *     corner. What a post cannot afford is a stamp at a LETTER's size, so the
 *     stamp is drawn at the width the bare face already had (`STAMP_W`): same
 *     footprint, same corner, and now the sheet and the letter it is a small
 *     version of are recognisably the same object. `LetterStamp` scales its
 *     perforations, frame and postmark from one width for exactly this.
 *   - **It is not cancelled.** A letter's stamp is franked when it arrived,
 *     because a cancellation is evidence a sorting office handled it. A post
 *     did not travel to you in the post; it was written in a room you can see
 *     into. The frame, the perforations and the portrait are the parts that say
 *     "paper"; the postmark would be saying something untrue.
 *   - **The overflow menu is bottom-end**, at the far end of the action row
 *     where the rest of the per-post controls already are. Block, mute and
 *     report do not belong in the corner a thumb rests in while reading.
 *
 * EVERY POST NAMES ITS ROOM. A space is required on a post now (see
 * `PostData.space`), so the sheet always has one to show, and it sits with the
 * postmark under the body — the two are the same class of fact, where a thing was
 * written. It is not a link, and since docs/5 § D260803-1933 that is a choice rather than a
 * consequence: a room has an address now, and the entrance to one is the search
 * field at the top of the feed. Where the prop `showSpace` is declared says why a
 * footer under every post is not a second one — and why the room's own screen turns
 * this line off.
 */

/**
 * The stamp's width in the letterhead. The height follows from `LetterStamp`'s own
 * postage ratio, so this is the only number here.
 *
 * 26px, WHICH IS THE WIDTH THE BARE FACE HAD. That is deliberate: the letterhead's
 * budget did not change, only what is spending it. A letter's stamp is 40px wide
 * and would be an ornament on a sheet a quarter the size — the note above says why
 * this card cannot afford one at that scale — so the stamp shrinks to the space the
 * photograph was already occupying rather than the card growing to fit a stamp.
 */
const STAMP_W = 26;
/** The bird before the name. Sized against the byline, not against the face. */
const BIRD = 21;
/**
 * The stamp on an arrival note, which has no byline to be sized against — it is
 * the introduction, centred beside a 48px bird, so it may have close to the 40px
 * a letter's stamp gets rather than the byline's `STAMP_W`.
 */
const WELCOME_STAMP_W = 42;

/*
 * There was a `SCOPE_LABEL` here and a badge under the body that rendered it:
 * "Replies from people they follow", "Replies from this space". Reply scopes are
 * gone — anyone who can see a post can reply to it — and `@dove/shared` carries the
 * argument where `REPLY_SCOPE` used to be defined. Nothing on a post says who may
 * answer it any more, because the answer is always everyone who can read it.
 */

/**
 * The byline: whose bird, whose name, and when.
 *
 * The bird is INSIDE the link with the name rather than beside it, because it
 * is a rendering of the same person and splitting them makes a 21px decorative
 * glyph a dead zone between two tap targets.
 */
function Identity({
  author,
  timeLabel,
  edited,
  bird,
  className,
}: {
  author: PublicAuthor;
  timeLabel?: string;
  /**
   * The words have been rewritten since they were written (docs/5 § D260805-0418).
   *
   * IN THE BYLINE, BESIDE THE AGE, because it is the same class of fact — when
   * this is from — and because a reader who wants to know whether they are
   * looking at the original is looking there already. Not under the body, where
   * it would read as part of what the person said.
   *
   * NOT A LINK AND NOT A TAP TARGET. Every previous version is kept, but reading
   * them is its own surface; the mark is a label, and a label is all that is
   * being claimed here.
   */
  edited?: boolean;
  /** Bird width in px. */
  bird: number;
  className?: string;
}) {
  return (
    <div className={cn('flex min-w-0 items-center gap-1.5 text-[15px]', className)}>
      <ToProfile
        author={author}
        className="flex min-w-0 items-center gap-1.5 font-semibold hover:underline"
      >
        {/*
          No halo. `Pigeon`'s `halo` exists for a bird that perches half on top
          of a photograph, and nothing does that any more (docs/5 § D260805-1523) — here
          it stands on bare paper, and a ring painted in `--background` would be a
          hole cut in the sheet.

          Flipped so the art, which faces physically left, looks back at what it
          belongs to. RTL mirrors the row and flips it back. Every byline in the
          product does this the same way.
        */}
        <BirdPortrait
          bird={author.bird}
          plumage={author.plumage}
          className="shrink-0 -scale-x-100 rtl:scale-x-100"
          style={{ width: bird }}
        />
        {/* `bdi`: a display name is user content of unknown direction sitting
            next to neutral characters (the `·` and the timestamp). Without
            isolation those neutrals resolve against the NAME's direction
            rather than the row's, which reorders the row. */}
        <bdi className="truncate">{author.name}</bdi>
      </ToProfile>
      {author.verified && (
        <BadgeCheck
          aria-label="Verified"
          className="size-4 shrink-0 fill-primary text-background"
        />
      )}
      {timeLabel && (
        <>
          <span aria-hidden className="text-paper-muted">
            ·
          </span>
          <time className="shrink-0 text-paper-muted">{timeLabel}</time>
        </>
      )}
      {/* No separating dot: the brackets already do that work, and a second `·`
          in a three-part byline is one more mark than the row can carry. */}
      {edited && (
        <span className="shrink-0 text-paper-muted">(edited)</span>
      )}
    </div>
  );
}

/**
 * An action. Neutral at rest; it only takes on colour once you've used it.
 *
 * NO COUNT. There is no number beside any of these, for anyone, including the
 * author (docs/1 § Posting, docs/5 § D260801-1119). A visible tally is a ranking of
 * people that renders itself, and it was the half of the influencer loop that
 * did the damage in public. What a writer gets back is comments.
 *
 * `aria-pressed` still carries the viewer's own state, which is the only
 * engagement state that exists on the wire.
 *
 * The hover wash is mixed from the sheet's own ink. The ghost variant's is
 * `muted`, a cold grey plate off the app's neutral scale, and on paper it
 * reads as a hole punched in the page.
 */
function Action({
  icon: Icon,
  label,
  active,
  activeClass,
  fill,
  indicator,
  onClick,
}: {
  icon: typeof Heart;
  label: string;
  active?: boolean;
  activeClass?: string;
  fill?: boolean;
  /** A conversation exists on this letter. This stays a dot, never a count. */
  indicator?: 'unseen' | 'seen';
  /** Absent on the actions that are not built yet, which then render as they always
   *  did: a control with no handler rather than a control that lies about what it
   *  did. */
  onClick?: () => void;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-pressed={active}
      aria-label={label}
      onClick={onClick}
      className={cn(
        '-mx-1 relative rounded-full px-2 font-normal text-paper-muted',
        'hover:bg-paper-ink/[0.06] hover:text-paper-ink dark:hover:bg-paper-ink/10',
        active && activeClass,
      )}
    >
      <Icon
        aria-hidden
        className={cn('size-[1.15rem]', active && fill && 'fill-current')}
      />
      {indicator && (
        <span
          aria-hidden
          className={cn(
            'absolute end-1 top-1 size-2 rounded-full ring-2 ring-card',
            indicator === 'unseen' ? 'bg-link' : 'bg-paper-muted',
          )}
        />
      )}
    </Button>
  );
}

/**
 * The overflow menu on SOMEBODY ELSE's letter, which is about the person who wrote
 * it: follow, mute, block, report.
 *
 * NAMED AND LIFTED OUT because a letter of your own carries `ReviseMenu` instead
 * (docs/5 § D260805-0418), and the two are alternatives rather than a list with
 * items added to it — none of these four is a thing anybody does to themselves,
 * and *Delete* has no business in the same menu as *Report*. Inline, the pair was
 * fifty lines of JSX inside a ternary at the end of an action row.
 *
 * Blocking, muting and reporting hang off every piece of public content rather than
 * living in settings (docs/2). "Not interested" and fewer letters like this one take
 * effect on the next page, not the next day (docs/3).
 */
function AuthorMenu({ author }: { author: PublicAuthor }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-me-1.5 ms-auto shrink-0 rounded-full text-paper-muted hover:bg-paper-ink/[0.06] hover:text-paper-ink dark:hover:bg-paper-ink/10"
          aria-label={`More from ${author.name}`}
        >
          <Ellipsis aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem>
          Follow <bdi>@{author.handle}</bdi>
        </DropdownMenuItem>
        <DropdownMenuItem>Not interested in this</DropdownMenuItem>
        <DropdownMenuItem>Why am I seeing this?</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem>
          Mute <bdi>@{author.handle}</bdi>
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive">
          Block <bdi>@{author.handle}</bdi>
        </DropdownMenuItem>
        <DropdownMenuItem variant="destructive">Report this letter</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/*
 * There was a `QuoteCard` here: a second byline and a second body, inset into
 * the sheet on a wash of its own ink. It is gone with quoting itself
 * (docs/5 § D260803-0131), and what removed it was a rendering problem rather than a
 * styling one.
 *
 * TWO PATTERNS WERE SAYING THE SAME THING. A quote drew two people's writing
 * inside one unit; so does a reply folded under the post it answers, three
 * screens further down. Nothing about either shape told a reader which one they
 * were looking at — whether the second name was in the conversation or was being
 * shown TO an audience by the first. The enclosure was drawn as quietly as it
 * could be and that did not help, because the ambiguity was structural: the same
 * form carried two meanings.
 *
 * Only the fold survives, so the form now carries one. Two bylines in one unit
 * is a conversation. Anything that wants to put a second person's writing on
 * this sheet has to answer for that, which is the point.
 */

/**
 * Liking and sending on, which are the only two things on this card that write.
 *
 * OPTIMISTIC, AND HELD IN THE SHARED QUERY CACHE. `aria-pressed` is the one
 * engagement state that exists on the wire, and it is a fact about YOU — so the button flips on the tap and
 * the request follows it. A control that waits for a round trip to fill in reads as
 * broken on a phone with one bar, and there is no count under it that could be wrong
 * in the meantime, because there are no counts (docs/5 § D260801-1119). That absence is what
 * makes an optimistic write here completely safe: the only thing on screen the server
 * could contradict is a boolean about yourself.
 *
 * THE SAME PATCH REACHES EVERY CACHED COPY. The same letter is drawn from the feed,
 * a room, a person's card and its conversation. `post-cache` traverses those query
 * families once, preserving untouched references, and rolls all of them back from
 * the same snapshot if the request fails.
 *
 * THE FEED IS NOT INVALIDATED AFTER. The response carries the authoritative boolean,
 * and patching it exactly avoids re-running every enrichment query on every loaded
 * feed page just because one heart changed.
 *
 * AND THE AUTHOR'S CARD, BECAUSE A LIKE NOW MOVES THE PAIR'S LEVEL (docs/5 § D260808-0213).
 * The meter used to settle overnight, so nothing a tap did could show up in this
 * session and there was nothing to refetch. It settles on the interaction now, and
 * a chip that only caught up on a cold load would make the change invisible on the
 * one surface it was built for. The loft goes with it — its rows draw the same
 * level (docs/5 § D260803-0021).
 *
 * NOT OPTIMISTIC, UNLIKE THE HEART ABOVE, and the asymmetry is the point. `liked` is
 * a boolean about yourself that no other input can contradict; the level is the
 * pair's, quantised, and it moves only when the server says the mutual term did.
 * Guessing it in the client would draw a rung somebody had not reached.
 */
function useEngagement(post: PostData) {
  const like = useMutation({
    mutationFn: (liked: boolean) => setLike(post.id, liked),
    onMutate: async (liked) => {
      await cancelPostQueries();
      const previousLiked = Boolean(post.liked);
      updatePostCaches(post.id, (cached) => ({
        ...cached,
        ...(liked ? { liked: true as const } : { liked: undefined }),
      }));
      return { previousLiked };
    },
    onError: (_error, _liked, context) => {
      if (!context) return;
      updatePostCaches(post.id, (cached) => ({
        ...cached,
        ...(context.previousLiked
          ? { liked: true as const }
          : { liked: undefined }),
      }));
    },
    onSuccess: ({ liked }) => {
      updatePostCaches(post.id, (cached) => ({
        ...cached,
        ...(liked ? { liked: true as const } : { liked: undefined }),
      }));
    },
    onSettled: () => {
      /* The boolean is patched exactly from the response. Only server-owned bond
         projections need reconciliation; refetching every feed page here made a
         heart tap one of the app's most expensive reads. */
      void queryClient.invalidateQueries({
        queryKey: keys.profile(post.author.handle),
      });
      void queryClient.invalidateQueries({ queryKey: keys.loft });
    },
  });

  return {
    liked: Boolean(post.liked),
    toggleLike: () => {
      if (!like.isPending) like.mutate(!post.liked);
    },
  };
}

/**
 * WHAT THE COMMENT CONTROL DOES.
 *
 *   - `'write'` (the default, on every surface that LISTS letters and on the
 *     letter's own screen): open the composer, addressed to this letter.
 *   - `'open'` (the main feed): open the conversation first. The conversation
 *     screen provides the writing control after its existing comments.
 *   - `'none'`: no control at all. A comment cannot be commented on — threads are
 *     single-level at MAX_REPLY_DEPTH and the server refuses it outright — so the
 *     comments under a letter offer nothing. Drawing a disabled glyph instead would
 *     be an affordance for a rule, and the rule is that there is no such act.
 *
 * IT IS A PROP RATHER THAN SOMETHING DERIVED FROM `post`, deliberately: the answer
 * depends on the SURFACE and not on the letter, and a component that guessed from
 * its data would have to be told anyway the day two surfaces disagree.
 *
 * `'none'` IS NOW UNUSED BY EVERY SURFACE, and it is kept rather than removed.
 * The conversation screen was its one caller — a comment there drew no glyph,
 * because answering one could only mean answering the letter and that control was
 * already on the screen. Since docs/5 § D260809-0427 the tap carries who was
 * being read (`answering`), so it is no longer a duplicate and the screen asks for
 * `'write'` like everything else. What keeps the value alive is that the QUESTION
 * is still a real one — a surface that lists writing nobody may answer from is a
 * surface this type already has the vocabulary for — and deleting it would mean
 * the next such surface either invents the prop again or quietly draws a control
 * that writes.
 */
export type CommentAction = 'write' | 'open' | 'focus' | 'none';

function PostCardView({
  post,
  connected = false,
  continued = false,
  showReplyContext = true,
  showSpace = true,
  commentAction = 'write',
  onComment,
  openThread = true,
}: {
  post: PostData;
  /**
   * A reply follows directly below.
   *
   * The pair is drawn as ONE SHEET FOLDED IN TWO: the two halves butt together
   * and a dashed rule runs across the seam. The thread line this replaces ran
   * down the avatar column, and there is no avatar column any more — a rule
   * hanging in the margin beside a sheet is not connected to anything, and
   * putting the column back to hold it would undo the change.
   */
  connected?: boolean;
  /** This post is the continuation of the sheet above; it drops its top edge. */
  continued?: boolean;
  /** Suppressed when the post being replied to is already sitting above. */
  showReplyContext?: boolean;
  /**
   * Whether the footer names the room this was written in.
   *
   * OFF INSIDE THE ROOM ITSELF (`SpaceScreen`, docs/5 § D260803-1933), where the header
   * already says which room you are standing in and every card repeating it is a
   * column of the same two words down the screen. On every other surface it stays:
   * the feed and a person's card both mix rooms, so there the room is the one fact
   * the footer exists to carry.
   */
  showSpace?: boolean;
  /** See `CommentAction`. */
  commentAction?: CommentAction;
  /** Used by a conversation's own Comment control to move to its composer. */
  onComment?: () => void;
  /**
   * Whether the letter READS as a way into its own conversation
   * (docs/5 § D260805-1523b).
   *
   * ON EVERYWHERE A LETTER IS LISTED, and it is the other half of the comment glyph
   * writing rather than navigating: the glyph is the act and the letter is the
   * reading surface. What makes it work is that the target is the writing — the
   * letterhead and the body — which is the region a reader is already looking at
   * when they want more of it.
   *
   * OFF ON THE CONVERSATION ITSELF, both for the letter at the top (you are already
   * there, and a card that navigates to the screen it is on is a tap that does
   * nothing) and for every comment under it (a comment's conversation is the letter
   * above, so the tap would move you nowhere and say it had).
   *
   * IT ALSO GATES THE FOLD (docs/5 § D260813-0012), and one flag governing both is
   * right rather than convenient: the fold and the tappable body are two ways into one
   * place, so a surface that is already that place must draw neither. It is why the
   * conversation screen needed no new prop when the fold arrived.
   */
  openThread?: boolean;
}) {
  const SpaceIcon = spaceIcon(post.space);
  const engage = useEngagement(post);
  const navigate = useNavigate();
  const openThreadSheet = useThreadSheet();
  const [editing, setEditing] = useState(false);

  /* THE CONVERSATION THIS CARD BELONGS TO, which for a comment is the letter above
     it and never the comment itself: one address per conversation, so answering a
     comment from a room's flat run of writing lands in the same thread the letter's
     own card would open. The server enforces the same identity — it answers a
     comment's id with its letter — and this is the client half, which saves a
     redirect on every tap. */
  const thread = post.replyToId ?? post.id;

  /* WHERE THE WRITING LEADS. Built once because it is spread into a `Link` inside
     the render and TanStack wants the params object with the route.

     IT STAYS A LINK EVEN WHEN A DRAWER WILL ANSWER THE TAP. A list that owns a
     masked sheet route intercepts the plain left click below and opens the
     conversation over itself; the element is still an anchor to the same durable
     address, so ⌘-click, middle-click, *Copy link* and the status bar keep working
     and assistive technology still reads a link to a place. A `<button>` here threw
     all of that away for a presentation choice. */
  const threadHref = openThread
    ? ({ to: '/letters/$id', params: { id: thread } } as const)
    : null;

  /*
   * WHETHER THIS IS YOURS TO CHANGE (docs/5 § D260805-0418).
   *
   * ONE COMPARISON, AND IT IS ENOUGH AGAIN (docs/5 § D260806-1109). It used to carry a
   * `!post.repostOf` guard, because a sent-on letter drew the resharer's byline over
   * somebody else's `post.id` — so on your own repost of another person's writing the
   * handle matched, the id did not belong to you, and the menu offered to rewrite a
   * letter you had not written. No card has two authors now, so the byline and the id
   * cannot disagree. The server refuses an edit that is not yours regardless
   * (`postIsMine`); this decides whether the control is drawn.
   */
  const { viewer, webOrigin } = useSession();
  const mine = viewer?.handle === post.author.handle;

  /*
   * WHO THE TAP WAS ANSWERING, WHEN THAT IS NOT WHAT IT ANSWERS
   * (docs/5 § D260809-0427).
   *
   * `thread` above keeps every answer in one flat conversation, which is the rule
   * and does not change. What it also threw away is the only thing this tap knew
   * that the letter's own Comment button did not: WHICH comment somebody was
   * reading. Under a run of twenty, that turns a specific answer into an
   * unaddressed remark, and the writer's only repair was to type the name out.
   *
   * SO THE PERSON IS CARRIED AS A NAME RATHER THAN AS A SECOND PARENT. The
   * composer still opens on the conversation; it opens with `@them ` already in
   * the box — an ordinary mention, editable and deletable like any other word.
   * That is what keeps this a courtesy rather than a hidden reply edge: nothing
   * about the ADDRESS changed, so the thread is still single-level and the server
   * is still the thing that says so (`MAX_REPLY_DEPTH`).
   *
   * NOT ON A LETTER, AND NOT ON YOUR OWN COMMENT. Answering a letter is already
   * addressed to its author, so tagging them would be the composer typing
   * something the reader can see for themselves; and naming yourself notifies
   * nobody (`lib/mentions` refuses it), so it is noise with no effect.
   */
  const answering = post.replyToId && !mine ? post.author.handle : undefined;

  /*
   * SHARING THE LETTER'S ADDRESS (docs/5 § D260805-1222). The mechanism and every
   * feature check live in `lib/share`, shared with the invitation sheet so the two
   * cannot disagree about what handing over a link means.
   *
   * IT IS THE CONVERSATION'S ADDRESS FOR A COMMENT TOO, which is `post.id` rather
   * than `thread`: the server answers a comment's id with the conversation it is in
   * (§ D260805-1112), so the shorter form loses nothing and the link still says which
   * comment somebody meant. On a sent-on letter `post.id` is the ORIGINAL letter's —
   * the byline is the resharer's — so what travels is the writing rather than the
   * resharing of it, which is the same inversion the trace line draws.
   */
  const shareUrl =
    canShareLink() || canCopyLink() ? letterUrl(post.id, webOrigin) : null;
  const [copied, setCopied] = useState(false);
  const share = () => {
    if (!shareUrl) return;
    void shareLink(shareUrl).then((outcome) => {
      /* ONLY THE SILENT OUTCOME REPORTS ITSELF. A share sheet is its own feedback,
         and a refused clipboard must not claim a copy that did not happen. */
      if (outcome !== 'copied') return;
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_MS);
    });
  };

  /* A comment is a letter with a parent, and it is called a comment on screen. */
  const noun = post.replyingTo ? 'comment' : 'letter';

  return (
    /* The Loft's gutter and rhythm: `px-4`, sheets a gap apart rather than
       divided by a rule. A folded pair closes the gap at the fold instead. */
    <article
      className={cn(
        'px-4',
        continued ? 'pt-0' : 'pt-3',
        connected ? 'pb-0' : 'pb-3',
      )}
    >
      {post.discoveryReason && (
        /* ON THE GROUND, ABOVE THE SHEET, and no longer indented to a text
           column that no longer exists. Why a post reached your feed is not
           something written on it, so it is not on the paper — which says the
           same thing the old indent was reaching for, and says it better.

           This is also how a discovery post is TOLD APART from a mutual's:
           the presence of the line is the signal, so a stranger never arrives
           in the feed unannounced. It may only ever describe content or shared
           conversations — never popularity, never the author as a person. */
        <p className="mb-1.5 flex items-center gap-2 px-1 text-[13px] text-muted-foreground">
          <Compass aria-hidden className="size-3.5 shrink-0" />
          {/* Our copy, not user data, so no bidi isolation: a discovery reason
              follows the document direction. */}
          <span className="truncate">{post.discoveryReason}</span>
        </p>
      )}

      {/*
        NO HOVER STATE ON THE SHEET, AND THAT NOW MEANS EVERYTHING OUTSIDE THE
        WRITING. The old row had one across the whole of it, and a whole-row wash
        is a promise that the row is a tap target; a post's targets are the name,
        the writing, the media and the action row, and never the surface between
        them. The Loft is the same shape and only tints the region that is genuinely
        a link — which since docs/5 § D260805-1523b includes the letterhead and the body,
        because tapping the writing opens the conversation (see `openThread`). The
        attachment, the footer and the room line are still not tap targets and carry
        no wash.

        NO OUTLINE EITHER, AND THAT IS THE CHANGE (docs/5 § D260803-0020). This carried a
        1px `border` on all four sides — the same hairline `LetterSheet` drew as a
        ring — and it went with the paper it was the edge of. A card is held by
        its shadow here, as `ui/card` and every other surface in the app is. The
        radius is the card radius for the same reason: `LetterSheet` argues both
        at length, and a post is that object at a quarter of the length.
      */}
      <div
        className={cn(
          /* `isolate` KEEPS THIS CARD'S LAYERING INSIDE THIS CARD, and it is
             required rather than tidy since docs/5 § D260805-1523b put a `z-20` on the
             identity links (so they clear the sheet that opens the conversation).
             Without it those participate in the page's own stacking context and win
             the tie against a sticky `PushedHeader`, which is also `z-20` and comes
             earlier in the DOM — a stamp painting over the header as you scroll is
             the exact failure that file's note describes, and it says the fix is to
             raise the isolation rather than the header. */
          'letter-note isolate rounded-xl px-3.5 py-3 shadow-sm',
          /*
            THE FOLD, WHICH IS THE ONE PLACE A RULE SURVIVES ON A POST, because
            it is not an edge — it is the crease between a post and the reply
            butted under it, and something has to be at the seam or the pair reads
            as one long post.

            The two halves also give up the corners they share: a rounded corner
            in the middle of a folded sheet is a gap with a curve in it. So
            `connected` flattens the bottom pair and `continued` the top, which
            leaves one card with a rounded top, a rounded bottom, and a dashed
            rule where it bends.

            `border-b border-dashed` rather than the old
            `[border-bottom-style:dashed]` override: with no border on the other
            three sides there is nothing left for `border-dashed` to affect, so
            the plain utility now means exactly the one edge. The old spelling was
            working around Tailwind having no per-side STYLE utility while all
            four sides had a width.
          */
          connected && 'rounded-b-none border-b border-dashed border-paper-ink/20',
          continued && 'rounded-t-none',
        )}
      >
        {/*
          THE READABLE REGION, WHICH IS ALSO THE WAY INTO THE CONVERSATION
          (docs/5 § D260805-1523b). The letterhead and the writing are wrapped together so one
          transparent sheet can be laid over both — `LETTER_OPEN`, the same
          affordance a `LetterSheet` and an `Envelope` use to open what they are
          showing, so "tap the writing to read the rest of it" means one thing in
          this product rather than three.

          IT STOPS BEFORE THE ATTACHMENT AND THE ACTION ROW, deliberately: a GIF's
          poster plays where it lies and the five actions each do their own thing, so
          neither may be swallowed by a navigation. That is also why this is a nested
          region rather than the overlay covering the card — the note above about the
          sheet having no hover state still holds for everything outside this box.

          `relative` is the overlay's container; the two links inside it are raised
          over the overlay rather than under it (see each of them).
        */}
        <div className="relative">
          {/* NOT WHILE THE LETTER IS BEING REWRITTEN. `ReviseField` puts a textarea
              and two buttons inside this region, and a transparent sheet over them is
              a field that cannot be typed in — the overlay is above everything that
              is not deliberately raised. */}
          {threadHref && !editing && (
            <Link
              {...threadHref}
              className={LETTER_OPEN}
              aria-label={`Open ${post.author.name}’s letter`}
              onClick={(e) => {
                /* A MODIFIED CLICK IS A REQUEST FOR THE ADDRESS ITSELF — a new tab,
                   a new window, a download — and it is left to the browser. Only the
                   plain one is the gesture the drawer exists for. */
                if (!openThreadSheet) return;
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                  return;
                }
                e.preventDefault();
                openThreadSheet(thread);
              }}
            />
          )}

          {post.welcome ? (
            <header>
              <ToProfile
                author={post.author}
                className="relative z-20 mx-auto flex w-fit items-center gap-3"
                aria-label={`${post.author.name}'s profile`}
              >
                <BirdPortrait
                  bird={post.author.bird}
                  plumage={post.author.plumage}
                  className="h-12 w-12 -scale-x-100 rtl:scale-x-100"
                />
                <LetterStamp author={post.author} width={WELCOME_STAMP_W} />
              </ToProfile>
              {/* The greeting keeps its centred mark, but it is still authored
                  writing. Preserve the same provenance as every other letter,
                  including the edit marker when its author revises it. */}
              <div className="relative z-20 mt-1 flex min-w-0 items-center justify-center gap-1.5 text-[13px] text-paper-muted">
                <ToProfile
                  author={post.author}
                  className="min-w-0 font-medium text-paper-ink hover:underline"
                >
                  <bdi className="truncate">{post.author.name}</bdi>
                </ToProfile>
                {post.author.verified && (
                  <BadgeCheck
                    aria-label="Verified"
                    className="size-3.5 shrink-0 fill-primary text-background"
                  />
                )}
                <span aria-hidden>·</span>
                <time className="shrink-0">{post.createdAtLabel}</time>
                {post.edited && <span className="shrink-0">(edited)</span>}
              </div>
            </header>
          ) : (
            <header className="flex items-start gap-2">
              <Identity
                author={post.author}
                timeLabel={post.createdAtLabel}
                edited={post.edited}
                bird={BIRD}
                className="relative z-20 min-w-0 flex-1"
              />
              <ToProfile
                author={post.author}
                className="relative z-20 shrink-0"
                aria-label={`${post.author.name}'s profile`}
              >
                <LetterStamp author={post.author} width={STAMP_W} />
              </ToProfile>
            </header>
          )}

          {post.replyingTo && showReplyContext && (
            /* *Commenting on*, because a public answer to an open letter is a
               comment (docs/5 § D260803-1952) — *replying* is what one half of a
               correspondence does to the other, in the Loft, where nobody else can
               read it. The two were one word and that word was the private one. */
            <p className="mt-0.5 text-[13px] text-paper-muted">
              Commenting on{' '}
              <ToProfile
                author={post.replyingTo}
                /* `text-link` names the profile reference by role; it aliases
                   the readable primary blue (see index.css).

                   `relative z-20` on the LINK and not on the line, so the two words
                   before it stay part of the region that opens the conversation while
                   the name still reaches the person. */
                className="relative z-20 text-link hover:underline"
              >
                <bdi>{post.replyingTo.name}</bdi>
              </ToProfile>
            </p>
          )}

          {/*
            The written part, and the ONLY part in the letter face: everything
            around it is chrome and inherits the UI face (see `letter-note`).

            `dir="auto"` because a post's direction belongs to the POST, not to
            the reader's locale. A public multilingual feed mixes scripts
            constantly (docs/8 § RTL), and an English post inside a document set
            to `rtl` otherwise renders with its sentence-final punctuation on
            the wrong side — the trailing "." jumps to the left. Each post takes
            the direction of its own first strong character instead.
          */}
          {editing ? (
            /* IN THE BODY'S OWN TYPE, at the body's own measure, so tapping Edit
               does not reflow the sheet under the thumb — see `ReviseField`, which
               takes the typeface from its caller for exactly this. */
            <ReviseField
              body={post.body}
              max={POST_DEFAULTS.POST_MAX_CHARS}
              className="mt-1 font-letter text-[15px]/[1.6]"
              onCancel={() => setEditing(false)}
              onSave={async (body) => {
                await cancelPostQueries();
                const previous = { body: post.body, edited: post.edited };
                updatePostCaches(post.id, (cached) => ({
                  ...cached,
                  body,
                }));
                setEditing(false);
                try {
                  const result = await editPost(post.id, body);
                  updatePostCaches(post.id, (cached) => ({
                    ...cached,
                    edited: result.edited || undefined,
                  }));
                } catch (error) {
                  updatePostCaches(post.id, (cached) => ({
                    ...cached,
                    ...previous,
                  }));
                  setEditing(true);
                  throw error;
                }
              }}
            />
          ) : (
            post.body && (
              <p
                dir="auto"
                className={cn(
                  'mt-1 font-letter text-[15px]/[1.6] whitespace-pre-wrap',
                  post.welcome && 'text-center',
                )}
              >
                {/* The names in it are tappable (docs/5 § D260809-0021). One
                    renderer for every body in the product, so what links here and
                    what the server notified are the same parse — `Written`.

                    `relative z-20` ON THE NAMES ONLY, and it is the same trick the
                    two identity links above use: `LETTER_OPEN` is an `absolute
                    inset-0 z-10` sheet covering this whole card, so a link drawn
                    under it takes no taps and an `@name` would open the letter
                    instead of the person. The prose around it stays under the
                    sheet on purpose — tapping the words of a letter is how the
                    letter opens. */}
                <Written body={post.body} className="relative z-20" />
              </p>
            )
          )}
        </div>

        {post.poll && <Poll post={post} />}

        {post.media?.length ? (
          /* Rounded, like the sheet it is pinned to, and a step tighter so it
             reads as sitting INSIDE the card rather than as a second card. It
             used to be `rounded-none`, for the reason the sheet was square. */
          <PostMediaGrid
            media={post.media}
            className="mt-2.5 rounded-lg border-paper-ink/10"
          />
        ) : null}

        {(showSpace || post.place) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-paper-muted">
            {/* THE ROOM IT WAS WRITTEN IN. The glyph comes from `lib/space`, shared
                with the composer — a sealed room keeps the padlock it earns, and the
                padlock is about who can SEE the room, not about who may comment,
                which is the only thing a lock on a letter used to mean here and no
                longer does.

                STILL NOT A LINK, though the reason changed under it. It used to be
                that a room had nowhere to link TO — a tappable space would have been
                the entrance to a directory of rooms (docs/5 § D260730-2028) — and since § D260803-1933
                a room is a place with an address. It stays a label anyway: the
                entrance to a room is the search field at the top of the feed, one
                entrance rather than two, and a navigation target under the last line
                of every post is a tap you make by accident while reading. `bdi`
                because a space title is content in an unknown script. */}
            {/* EVERY LETTER IS IN A ROOM AGAIN (docs/5 § D260806-1109b), so this is one
                shape rather than two: the enclosed chip and the `personal` branch that
                drew it are gone with page-only letters. */}
            {showSpace && (
              <p className="flex items-center gap-1.5">
                <SpaceIcon aria-hidden className="size-3 shrink-0" />
                <bdi>{post.space.title}</bdi>
              </p>
            )}

            {/* City, never finer: the postmark rule (docs/5 D260731-0207). It is not
                a link, because a tappable place is a directory of who was
                where, which is a browsing surface for people. And on a letter
                it is literally a postmark, which is where `LetterSheet` puts
                the same field. */}
            {post.place && (
              <p className="flex items-center gap-1.5">
                <MapPin aria-hidden className="size-3 shrink-0" />
                <bdi>{post.place}</bdi>
              </p>
            )}
          </div>
        )}

        {/*
          Grouped rather than spread edge to edge. With the counts gone the
          actions are five identical glyphs, and `justify-between` strung them
          across 300px with no relationship to each other.

          Share sits WITH bookmark now rather than pushed to the far end. The
          two are the same gesture — keeping a piece of writing, here or
          elsewhere — and the end of the row is a better home for the menu,
          which is the one control on a post that acts on the PERSON rather
          than on the post: follow, mute, block, report.
        */}
        <footer className="-mb-1 mt-1.5 flex items-center gap-4">
          {/* ANYONE WHO CAN SEE THIS MAY COMMENT ON IT, which is the whole of the
              audience model: there is no reply scope, and seeing an open letter is
              what permits answering it (docs/1 § Open letters). The label is
              *Comment* rather than *Reply* — see the note on the byline above.

              ONE TAP, AND IT IS THE ACT (docs/5 § D260805-1523b). The label was *Comments* on
              a feed, because the glyph opened the conversation there and a control
              that says *Comment* and opens a reading surface is a control that lied.
              The glyph writes on every surface now, so the label is the act
              everywhere, and the reading surface is one tap on the letter itself. */}
          {commentAction !== 'none' && (
            <Action
              icon={MessageCircle}
              label={post.hasComments
                ? post.commentsSeen ? 'Comment, caught up' : 'Comment, new comments'
                : 'Comment'}
              indicator={post.hasComments ? post.commentsSeen ? 'seen' : 'unseen' : undefined}
              onClick={() => {
                if (commentAction === 'focus') {
                  onComment?.();
                  return;
                }
                if (commentAction === 'open') {
                  if (openThreadSheet) {
                    openThreadSheet(thread);
                    return;
                  }
                  void navigate({
                      to: '/letters/$id',
                      params: { id: thread },
                  });
                  return;
                }
                void navigate({
                  to: '/compose',
                  /* `kind` is explicit even though `on` implies it: the router
                     defaults a missing kind to `post`, and relying on that would
                     make this link depend on a fallback rather than say what it
                     means.

                     `thread` AND NOT `post.id`, which is load-bearing now that the
                     glyph writes on every surface: a room shows a flat run of
                     writing, comments included, and a comment cannot be commented on
                     (MAX_REPLY_DEPTH — the server refuses it). Addressed to the
                     conversation, that tap answers the letter the comment is in,
                     which is the only answer there is. It used to be `post.id`, safe
                     only because the glyph navigated rather than wrote.

                     `at` IS WHO WAS BEING READ, and it is not a second address —
                     see `answering`. It seeds a mention and nothing else, so the
                     letter this writes is addressed exactly where it was before. */
                  search: { kind: 'post', on: thread, ...(answering ? { at: answering } : {}) },
                });
              }}
            />
          )}
          {/*
            THERE WAS A *Send on* CONTROL HERE, and what can be done with a letter is
            now exactly four things (docs/5 § D260806-1109): Comment, Like, Save, Share.
            Sending somebody else's writing to your own readers is not one of them,
            and nothing replaced it — the space it freed goes to the four that are
            left rather than to a fifth glyph.

            The row is deliberately not re-ordered around the gap: Comment is still
            first, because answering is the act this product is for.
          */}
          <Action
            icon={Heart}
            label="Like"
            active={engage.liked}
            activeClass="text-link hover:text-link"
            fill
            onClick={engage.toggleLike}
          />
          <Action
            icon={Bookmark}
            label="Save"
            active={post.saved}
            activeClass="text-primary hover:text-primary"
            fill
          />
          {/*
            SHARING HANDS THE LETTER'S ADDRESS TO SOMEBODY OUTSIDE THE APP
            (docs/5 § D260805-1222) — the OS share sheet where there is one, the clipboard
            where there is not, and nothing at all where neither exists. It became
            possible rather than merely unbuilt when a conversation got an address
            (§ D260805-1112): before that there was no URL for this control to hand over.

            IT SHARES THE ADDRESS, NOT THE WRITING. No excerpt, no image, no
            pre-written sentence: what leaves is a link, and what a link opens is
            login-walled and `noindex` like every other user surface (docs/2
            § Indexability). Whoever follows it sees the letter only if they may
            already see the letter.

            NOTHING COUNTS IT. There is no share tally on the wire, in the schema or
            on this card — the same rule the other four actions obey (§ D260801-1119).

            ABSENT ON A SEALED ROOM'S LETTER, which is the one case where this control
            would break a promise the product has already made in writing: the
            composer tells everybody writing into a single-gender room that "nothing
            here can be sent on, and it never leaves the app", and docs/2 § Spaces makes
            exfiltration a permanent-ban offence. A link out is the app's own hand on
            the thing leaving. Absent rather than disabled, for the reason
            `InviteSheet` gives about a control that cannot work.
          */}
          {shareUrl && !post.space.sealed && (
            <Action
              icon={copied ? Check : Share}
              /* THE LABEL SAYS WHICH THING THE DEVICE WILL DO, because on a desktop
                 this control copies and the word *Share* would be a promise of a
                 sheet that is not coming — and it says *Copied* for as long as the
                 tick is up, so the one outcome with no visible consequence is
                 announced rather than merely drawn. */
              label={copied ? 'Copied' : canShareLink() ? 'Share' : 'Copy link'}
              onClick={share}
            />
          )}

          {/*
            YOUR OWN LETTER GETS A DIFFERENT MENU, NOT A LONGER ONE
            (docs/5 § D260805-0418). Everything in the menu below is something you do
            to the person who wrote this — follow, mute, block, report — and none of
            them is a thing anybody does to themselves; a card of your own drew all
            four anyway until editing existed to replace them. It also keeps *Delete*
            out of the same list as *Report*, which is where a destructive tap goes
            wrong.
          */}
          {mine ? (
            <ReviseMenu
              noun={noun}
              className="-me-1.5 ms-auto"
              onEdit={() => setEditing(true)}
              onDelete={async () => {
                const result = await deletePost(post.id);
                removePostCaches(post.id, result);
              }}
            />
          ) : (
            <AuthorMenu author={post.author} />
          )}
        </footer>

      </div>
    </article>
  );
}

/* Loading the next feed page must not re-run every existing card. TanStack Query
   preserves the old PostData references, and memo turns that structural sharing
   into skipped React work; an optimistic cache patch still re-renders its one card. */
export const PostCard = memo(PostCardView);
